import type { ConfirmDialogOptions } from "@judo/feedback";

/**
 * Set of action definition types that are considered destructive.
 * These require user confirmation before execution.
 */
export const DESTRUCTIVE_ACTION_TYPES = new Set([
	"DeleteActionDefinition",
	"RowDeleteActionDefinition",
	"BulkDeleteActionDefinition",
	"RemoveActionDefinition",
	"BulkRemoveActionDefinition",
	"UnsetActionDefinition",
	"ClearActionDefinition",
]);

/**
 * Checks whether an action type is destructive (delete, remove, unset, clear).
 * Handles both prefixed ("ui:DeleteActionDefinition") and unprefixed forms.
 */
export function isDestructiveAction(actionType?: string): boolean {
	if (actionType == null) return false;
	// Strip namespace prefix if present (e.g., "ui:DeleteActionDefinition" → "DeleteActionDefinition")
	const bare = actionType.includes(":") ? actionType.split(":").pop()! : actionType;
	return DESTRUCTIVE_ACTION_TYPES.has(bare);
}

/**
 * Returns appropriate confirmation dialog options for a destructive action type.
 * Handles both prefixed ("ui:DeleteActionDefinition") and unprefixed forms.
 */
export function getDestructiveConfirmationOptions(actionType: string): ConfirmDialogOptions {
	// Strip namespace prefix if present
	const bare = actionType.includes(":") ? actionType.split(":").pop()! : actionType;

	switch (bare) {
		case "DeleteActionDefinition":
		case "RowDeleteActionDefinition":
			return {
				title: "Confirm Delete",
				message: "Are you sure you want to delete this item?",
				confirmText: "Delete",
				confirmColor: "error",
				destructive: true,
			};
		case "BulkDeleteActionDefinition":
			return {
				title: "Confirm Delete",
				message: "Are you sure you want to delete the selected items?",
				confirmText: "Delete All",
				confirmColor: "error",
				destructive: true,
			};
		case "RemoveActionDefinition":
			return {
				title: "Confirm Remove",
				message: "Are you sure you want to remove this item from the relation?",
				confirmText: "Remove",
				confirmColor: "error",
				destructive: true,
			};
		case "BulkRemoveActionDefinition":
			return {
				title: "Confirm Remove",
				message: "Are you sure you want to remove the selected items from the relation?",
				confirmText: "Remove All",
				confirmColor: "error",
				destructive: true,
			};
		case "UnsetActionDefinition":
			return {
				title: "Confirm Unset",
				message: "Are you sure you want to unset this relation?",
				confirmText: "Unset",
				confirmColor: "warning",
				destructive: true,
			};
		case "ClearActionDefinition":
			return {
				title: "Confirm Clear",
				message: "Are you sure you want to clear this relation?",
				confirmText: "Clear",
				confirmColor: "warning",
				destructive: true,
			};
		default:
			return {
				title: "Confirm Action",
				message: "Are you sure you want to proceed?",
				confirmText: "Confirm",
				destructive: true,
			};
	}
}
